import { nextTick, onBeforeUnmount, onMounted, provide, watch, type Ref, type TemplateRef } from "vue";
import { DS_DIALOG_REF_PROVIDE_KEY } from "@/src/design_system/layouts_and_containers/dialog/constants";
import { FOCUSABLE_ELS } from "@/src/design_system/layouts_and_containers/dialog/constants/focusTrap";
import { DS_LIGHT_BOX_CLASSES } from "../constants/classes";

export const useLightBoxDialog = (open: Ref<boolean>, dialogRef: TemplateRef<HTMLDialogElement>) => {
  let trigger: HTMLElement | null = null;
  provide(DS_DIALOG_REF_PROVIDE_KEY, dialogRef);

  const visibleControls = () =>
    Array.from(
      dialogRef.value?.querySelectorAll<HTMLElement>(`${FOCUSABLE_ELS}, input, audio[controls], video[controls]`) ?? []
    )
      .filter(element => !element.classList.contains(DS_LIGHT_BOX_CLASSES.FocusBoundary))
      .filter(element => !element.matches(":disabled, [disabled], input[type=hidden]"))
      .filter(element => !(element.hasAttribute("tabindex") && element.tabIndex < 0))
      .filter(element => !element.closest("[inert], [hidden]"))
      .filter(element => element.getClientRects().length > 0 && getComputedStyle(element).visibility !== "hidden");
  const focusFirstControl = () => visibleControls()[0]?.focus();
  const focusLastControl = () => visibleControls().at(-1)?.focus();
  const onStartBoundaryFocus = () => focusLastControl();
  const onEndBoundaryFocus = () => focusFirstControl();

  const closeNative = () => {
    if (!dialogRef.value?.open) return;
    dialogRef.value.close();
    trigger?.focus();
  };
  const syncDialog = async () => {
    await nextTick();
    const dialog = dialogRef.value;
    if (!dialog) return;
    if (!open.value) return closeNative();
    if (dialog.open) return;
    trigger = document.activeElement as HTMLElement | null;
    dialog.showModal();
  };
  watch(open, syncDialog);
  onMounted(syncDialog);
  onBeforeUnmount(closeNative);
  const hasFocus = () => Boolean(dialogRef.value?.contains(document.activeElement));
  const restoreRemovedFocus = async () => {
    const hadFocus = hasFocus();
    await nextTick();
    if (hadFocus && open.value && !hasFocus()) focusFirstControl();
  };
  return {
    focusFirstControl,
    focusLastControl,
    onStartBoundaryFocus,
    onEndBoundaryFocus,
    restoreRemovedFocus,
  };
};
